import { Component, OnInit } from '@angular/core';
import { AppContextService } from '../../../services/app-context.service';
import { ReportingApiService } from '../../../server-api/reporting-api/reporting-api.service';
import { ServerApiService } from '../../../server-api/server-api.service';
import { TranslateService } from '../../../services/translate/translate.service';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';
import { OperationError } from '../../../models/server-result/operation-error';
import { User } from '../../../models/User/user';
import { OperationResult } from '../../../models/server-result/operation-result';
import { HttpErrorResponse } from '@angular/common/http';
import { VerticalBarChart } from '../../../models/charts/vertical-bar-chart';
import { DateLocalePipe } from '../../../services/date-locale.pipe';
import { DocumentForecastByState } from '../../../models/dashboard/document-forecast-by-paymentstate';
import { FactoringContractsSearchCriteria } from '../../../models/contracts/factoring/factoring-contracts-search-criteria';
import { FactoringContract } from '../../../models/contracts/factoring/factoring-contract';
import { ConfirmingConnectionsSearchCriteria } from '../../../models/contracts/confirming/confirming-connections-search-criteria';
import { ConfirmingConnection } from '../../../models/contracts/confirming/confirming-connection';
import { CurrencyCode } from '../../../models/enums/currency-code';
import { Router } from '@angular/router';
import { FileIntegrationStateService } from '../../../services/file-integration-state.service';
import { SearchFilesIntegrationComponent } from '../../files-integration/search-files-integration/search-files-integration.component';
import { FileStatus } from '../../../models/enums/FileIntegration/integration-file-status';
import { CurrencyViewModel, DASHBOARD_TAB_CONFIRMING, DASHBOARD_TAB_FACTORING } from '../dashboard.types';
import { DashboardHelperService } from '../dashboard-helper.service';
import { CustomTabItem, TabAction } from '../../../shared-components/custom-tabs/custom-tabs.component';
import { SegmentedControlOption } from '../../../shared-components/custom-segmented-control/custom-segmented-control.component';

@Component({
	selector: 'app-dashboard-supplier',
	templateUrl: './dashboard-supplier.component.html',
	styleUrls: ['../private-home.component.scss'],
	standalone: false,
})
export class DashboardSupplierComponent implements OnInit {
	public loggedUser: User | null = new User();
	public requestErrors: OperationError[] = [];

	public CurrentMonth: Date;
	public notPaidDocuments: number = 0;

	// Confirming properties
	public confirmingApprovedRequestFunds: number = 0;
	public currentFundingRequestsAmount: number = 0;
	public suppliersActiveConnections: number = 0;

	// Factoring properties
	public factoringBuyersActiveConnections: number = 0;
	public factoringAdvanceableAmount: number = 0;
	public factoringAdvancedAmount: number = 0;
	public factoringPortfolioAmount: number = 0;
	public factoringPendingApprovalDocumentsAmount: number = 0;

	public selectedCurrency: string = CurrencyCode.Euro;

	public documentForecastChart: VerticalBarChart = new VerticalBarChart();




	// Tab management
	public confirmingTabVisible: boolean = false;
	public factoringTabVisible: boolean = false;
	public activeTabId: string = '';
	public tabs: CustomTabItem[] = [];
	public tabActions: TabAction[] = [];

	protected DASHBOARD_TAB_CONFIRMING = DASHBOARD_TAB_CONFIRMING;
	protected DASHBOARD_TAB_FACTORING = DASHBOARD_TAB_FACTORING;

	public orderedCurrencies: CurrencyViewModel[] = [];
	public currencyOptions: SegmentedControlOption[] = [];

	protected FileStatus = FileStatus;

	// Loading states for dashboard cards
	public loadingFactoringPortfolioAmount: boolean = false;
	public loadingFactoringAdvancedAmount: boolean = false;
	public loadingFactoringAdvanceableAmount: boolean = false;
	public loadingFactoringBuyersActiveConnections: boolean = false;
	public loadingFactoringPendingApprovalDocumentsAmount: boolean = false;
	public loadingNotPaidDocuments: boolean = false;

	// Confirming loading states
	public loadingConfirmingApprovedRequestFunds: boolean = false;
	public loadingCurrentFundingRequestsAmount: boolean = false;
	public loadingSuppliersActiveConnections: boolean = false;

	constructor(
		private reportingApiService: ReportingApiService,
		private serverApiService: ServerApiService,
		private appContext: AppContextService,
		private translateService: TranslateService,
		private toasterService: ToasterService,
		private dateLocale: DateLocalePipe,
		private router: Router,
		private fileIntegrationStateService: FileIntegrationStateService,
		private dashboardHelper: DashboardHelperService
	) {}

	ngOnInit() {
		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;

			this.CurrentMonth = this.dateLocale.transform(new Date(), 'MM/yyyy');

			// Set tab visibility based on user permissions
			if (this.loggedUser?.canSeeConfirmingOptions()) {
				this.confirmingTabVisible = true;
				this.clickedConfirmingTab();
			}

			if (this.loggedUser?.canSeeFactoringOptions()) {
				this.factoringTabVisible = true;

				if (this.activeTabId !== DASHBOARD_TAB_CONFIRMING) this.clickedFactoringTab();
			}

			this.tabs = this.dashboardHelper.createTabs(this.confirmingTabVisible, this.factoringTabVisible);
			this.tabActions = [this.dashboardHelper.createRefreshAction(() => this.refreshAllData())];

			// Initialize chart configuration
			this.documentForecastChart = this.dashboardHelper.createVerticalBarChart(
				this.translateService.translate('dashboard-buyer.chart.buyerConfirmedPaymentDate'),
				this.selectedCurrency
			);
		});
	}


	private getNotPaidDocumentsSum(currency: string): void {
		this.notPaidDocuments = 0;
		this.dashboardHelper.loadMetric(
			this.reportingApiService.getNotPaidDocumentsSum(currency),
			(loading) => (this.loadingNotPaidDocuments = loading),
			(value) => (this.notPaidDocuments = value),
			'[DashboardSupplierComponent] Invalid result from getNotPaidDocumentsSum()'
		);
	}

	private getConfirmingSuppliersActiveConnections(currency: string): void {
		this.suppliersActiveConnections = 0;
		this.dashboardHelper.loadMetric(
			this.reportingApiService.getConfirmingSuppliersActiveConnections(currency),
			(loading) => (this.loadingSuppliersActiveConnections = loading),
			(value) => (this.suppliersActiveConnections = value),
			'[DashboardSupplierComponent] Invalid result from getConfirmingSuppliersActiveConnections()'
		);
	}

	private getConfirmingCurrentFundingRequestsAmount(currency: string): void {
		this.currentFundingRequestsAmount = 0;
		this.dashboardHelper.loadMetric(
			this.reportingApiService.getConfirmingCurrentFundingRequestsAmount(currency),
			(loading) => (this.loadingCurrentFundingRequestsAmount = loading),
			(value) => (this.currentFundingRequestsAmount = value),
			'[DashboardSupplierComponent] Invalid result from getConfirmingCurrentFundingRequestsAmount()'
		);
	}

	private getConfirmingApprovedRequestFunds(currency: string) {
		this.confirmingApprovedRequestFunds = 0;
		const fundingRequestDocumentStates = ['Approved'];

		this.dashboardHelper.loadMetric(
			this.reportingApiService.getConfirmingFundingRequestsTotalAdvancedAmount(fundingRequestDocumentStates, currency),
			(loading) => (this.loadingConfirmingApprovedRequestFunds = loading),
			(value) => (this.confirmingApprovedRequestFunds = value),
			'[DashboardSupplierComponent] Invalid result from getConfirmingFundingRequestsTotalAdvancedAmount',
			'[DashboardSupplierComponent] HttpError result from getConfirmingFundingRequestsTotalAdvancedAmount'
		);
	}

	private getDocumentForecast(currency: string) {
		// Reset chart data before fetching new data
		this.documentForecastChart.Results = [];
		this.documentForecastChart.CustomColors = [];

		this.dashboardHelper.loadMetric(
			this.reportingApiService.getDailyDocumentForecast(currency),
			() => {},
			(data) => this.processDocumentForecast(data),
			'[DashboardSupplierComponent] Invalid result from getDocumentForecast',
			'[DashboardSupplierComponent] HttpError result from getDocumentForecast'
		);
	}

	private processDocumentForecast(data: DocumentForecastByState[]) {
		this.documentForecastChart.ShowLegend = data.some((d) => !!d.DocumentPaymentState);
		this.documentForecastChart.Results = this.dashboardHelper.buildDocumentForecastLineSeries(data, (d) =>
			this.dateLocale.transform(d)
		);
		this.documentForecastChart.CustomColors = this.dashboardHelper.buildDocumentForecastCustomColors(data);
	}

	private getPendingApprovalDocumentsAmount(currency: string) {
		this.factoringPendingApprovalDocumentsAmount = 0;
		this.dashboardHelper.loadMetric(
			this.reportingApiService.getPendingApprovalDocumentsAmount(currency),
			(loading) => (this.loadingFactoringPendingApprovalDocumentsAmount = loading),
			(value) => (this.factoringPendingApprovalDocumentsAmount = value.Factoring),
			'[DashboardSupplierComponent] Invalid result from getPendingApprovalDocumentsAmount',
			'[DashboardSupplierComponent] HttpError result from getPendingApprovalDocumentsAmount'
		);
	}

	private getFactoringContracts() {
		// Set loading states for values that are calculated in this method
		this.loadingFactoringPortfolioAmount = true;
		this.loadingFactoringAdvancedAmount = true;
		this.loadingFactoringAdvanceableAmount = true;

		const criteria: FactoringContractsSearchCriteria = new FactoringContractsSearchCriteria();
		criteria.OrderBy = 'SupplierName';
		criteria.PageNumber = 1;
		criteria.OrderDescending = true;
		criteria.RowsPerPage = 2147483647;
		criteria.RestrictionCriteria = new FactoringContractsSearchCriteria();
		criteria.RestrictionCriteria.ContractStateList = ['Active'];
		criteria.RestrictionCriteria.SupplierIdList = this.loggedUser?.Companies.map((c) => c.Id!) ?? [];

		this.serverApiService.searchFactoringContracts(criteria).subscribe({
			next: (operationResult: OperationResult<FactoringContract[]>) => {
				if (operationResult.IsValid) {
					if (!!operationResult.Data && operationResult.Data.length > 0) {
						operationResult.Data.forEach((contract) => {
							this.factoringPortfolioAmount += contract.DocumentPortfolioAmount;
							this.factoringAdvanceableAmount += contract.AdvanceableAmount;
							this.factoringAdvancedAmount += contract.AdvancedAmount;
						});
					}
				} else {
					this.toasterService.showError(this.translateService.translate('ERROR_GET_COMPANY_CONTRACTS', 'list-errorCodes'));
				}

				// Set loading states to false after all calculations are complete
				this.loadingFactoringPortfolioAmount = false;
				this.loadingFactoringAdvancedAmount = false;
				this.loadingFactoringAdvanceableAmount = false;
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);

				// Set loading states to false on error
				this.loadingFactoringPortfolioAmount = false;
				this.loadingFactoringAdvancedAmount = false;
				this.loadingFactoringAdvanceableAmount = false;
			},
		});
	}

	private getFactoringBuyersActiveConnections(): void {
		this.factoringBuyersActiveConnections = 0;
		this.dashboardHelper.loadMetric<number>(
			this.reportingApiService.getFactoringBuyersActiveConnections(),
			(loading) => (this.loadingFactoringBuyersActiveConnections = loading),
			(value) => (this.factoringBuyersActiveConnections = value),
			'[DashboardSupplierComponent] Invalid result from getFactoringBuyersActiveConnections()',
			'[DashboardSupplierComponent] Invalid result from getFactoringBuyersActiveConnections() '
		);
	}

	private loadAllFactoringData(): void {
		this.resetValues();

		this.getNotPaidDocumentsSum(CurrencyCode.Euro);
		this.getDocumentForecast(CurrencyCode.Euro);
		this.getPendingApprovalDocumentsAmount(CurrencyCode.Euro);

		this.getFactoringBuyersActiveConnections();
		this.getFactoringContracts();
	}

	private loadAllConfirmingData(): void {
		this.resetValues();

		const criteria: ConfirmingConnectionsSearchCriteria = new ConfirmingConnectionsSearchCriteria();
		criteria.PageNumber = 1;
		criteria.RowsPerPage = 2147483647;
		criteria.RestrictionCriteria = new ConfirmingConnectionsSearchCriteria();
		criteria.RestrictionCriteria.ConnectionStateList = ['Active', 'Inactive'];
		criteria.RestrictionCriteria.ContractStateList = ['Active', 'Inactive'];

		this.serverApiService.searchConfirmingConnections(criteria).subscribe({
			next: (operationResult: OperationResult<ConfirmingConnection[]>) => {
				if (operationResult.IsValid) {
					if (!!operationResult.Data && operationResult.Data.length > 0) {
						let currencies: CurrencyViewModel[] = [];

						for (let index = 0; index < operationResult.Data.length; index++) {
							let connection: ConfirmingConnection = operationResult.Data[index];

							this.dashboardHelper.addCurrencyIfMissing(currencies, connection.Currency);
						}

						this.orderedCurrencies = this.dashboardHelper.sortCurrencies(currencies);
						this.currencyOptions = this.dashboardHelper.toCurrencyOptions(this.orderedCurrencies);

						if (!!this.orderedCurrencies && this.orderedCurrencies.length > 0) {
							let initialCurrencytoProcess = this.orderedCurrencies[0].Currency;
							this.clickedCurrency(initialCurrencytoProcess);
							this.getDocumentForecast(initialCurrencytoProcess);
						}
					}
				} else {
					this.toasterService.showError(this.translateService.translate('ERROR_GET_COMPANY_CONTRACTS', 'list-errorCodes'));
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
			},
		});
	}

	public clickedConfirmingTab(): void {
		this.activeTabId = DASHBOARD_TAB_CONFIRMING;
		this.loadAllConfirmingData();
	}

	public clickedFactoringTab(): void {
		this.activeTabId = DASHBOARD_TAB_FACTORING;
		this.selectedCurrency = CurrencyCode.Euro;
		this.loadAllFactoringData();
	}

	public onTabChange(tabId: string): void {
		if (tabId === DASHBOARD_TAB_CONFIRMING) this.clickedConfirmingTab();
		else if (tabId === DASHBOARD_TAB_FACTORING) this.clickedFactoringTab();
	}

	public clickedCurrency(currency: string): void {
		this.resetValues();

		this.selectedCurrency = currency;
		this.documentForecastChart.YAxisLabel = this.selectedCurrency;

		this.getNotPaidDocumentsSum(currency);
		this.getDocumentForecast(currency);
		this.getPendingApprovalDocumentsAmount(currency);

		this.getConfirmingCurrentFundingRequestsAmount(currency);
		this.getConfirmingApprovedRequestFunds(currency);
		this.getConfirmingSuppliersActiveConnections(currency);
	}

	private resetValues() {
		this.notPaidDocuments = 0;

		// Reset confirming values
		this.confirmingApprovedRequestFunds = 0;
		this.currentFundingRequestsAmount = 0;
		this.suppliersActiveConnections = 0;

		// Reset factoring values
		this.factoringPortfolioAmount = 0;
		this.factoringAdvancedAmount = 0;
		this.factoringAdvanceableAmount = 0;
		this.factoringBuyersActiveConnections = 0;
		this.factoringPendingApprovalDocumentsAmount = 0;

		// Reset chart data
		this.documentForecastChart.Results = [];
		this.documentForecastChart.CustomColors = [];
	}

	public navigateToFilesIntegration(key: string): void {
		SearchFilesIntegrationComponent.navigateWithFileStatus(this.router, this.fileIntegrationStateService, key);
	}

	public refreshAllData(): void {
		if (this.activeTabId === DASHBOARD_TAB_CONFIRMING) {
			this.loadAllConfirmingData();
		} else if (this.activeTabId === DASHBOARD_TAB_FACTORING) {
			this.loadAllFactoringData();
		}
	}
}
